// src/components/organisms/PatientWorkspaceTabs/InvestigationEditor.js
//
// Tetkik kayıt editörü. Master-detail layout'unda sağ panel olarak render edilir.
// Tüm autosave mantığı `useInlineAutosave` hook'undadır — bu bileşen saf form.
// Düz editör dili (kutusuz bölümler, drawer ile ekleme): bkz. _editorParts.

import React, { useEffect, useCallback, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  DeleteOutline as DeleteOutlineIcon,
  ScienceRounded as LabIcon,
  ImageSearchRounded as ImagingIcon,
} from "@mui/icons-material";

import FormSelect from "../../atoms/FormSelect";
import FormTextField from "../../atoms/FormTextField";
import FormCheckbox from "../../atoms/FormCheckbox";
import ActionButton from "../../atoms/ActionButton";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import {
  useCreateTetkikMutation,
  useUpdateTetkikMutation,
} from "../../../store/secretaryApi";
import {
  useListLabTestsMutation,
  useListImagesMutation,
} from "../../../store/systemVariablesApi";
import TagPickerDrawer from "./drawers/TagPickerDrawer";
import {
  EDITOR_PX,
  SECTION_SX,
  listAreaSx,
  ITEM_NAME_SX,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
} from "./_editorParts";

const TYPE_OPTIONS = [
  { value: "laboratory", label: "Laboratuvar" },
  { value: "imaging", label: "Görüntüleme" },
];

const STATUS_OPTIONS_ALL = [
  { value: "requested", label: "İstendi" },
  { value: "in_progress", label: "Yapılıyor" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal" },
];
const STATUS_OPTIONS_CREATE = STATUS_OPTIONS_ALL.filter(
  (s) => s.value !== "cancelled"
);

// Seçim drawer'ında sunucudan sayfa başına gelen kayıt
const PICKER_PAGE_SIZE = 30;
// Kalem listesi son bölüm: editörün kalan yüksekliğini doldurur, en az bu kadar
const ITEM_LIST_MIN_HEIGHT = 240;

// İki satırlı kalem: üstte tamamlandı + ad + ref + sil, altta sonuç / not
const ITEM_BLOCK_SX = {
  py: 1.25,
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  borderBottom: `1px solid ${COLORS.slate100}`,
  "&:last-of-type": { borderBottom: "none" },
};

const tetkikFieldsFromRecord = (record) => ({
  type: record?.type || "laboratory",
  status: record?.status || "requested",
  note: record?.note || "",
  laboratoryItems: Array.isArray(record?.laboratoryItems)
    ? record.laboratoryItems.map((it) => ({
        labTest: it.labTest?._id || it.labTest,
        name: it.name ?? it.labTest?.name ?? "",
        unit: it.unit ?? it.labTest?.unit ?? "",
        referenceValue: it.referenceValue ?? it.labTest?.referenceValue ?? "",
        resultValue: it.resultValue ?? "",
        notes: it.notes ?? "",
        isCompleted: Boolean(it.isCompleted),
      }))
    : [],
  imagingItems: Array.isArray(record?.imagingItems)
    ? record.imagingItems.map((it) => ({
        image: it.image?._id || it.image,
        name: it.name ?? it.image?.name ?? "",
        label: it.label ?? "",
        notes: it.notes ?? "",
        isCompleted: Boolean(it.isCompleted),
      }))
    : [],
});

export default function InvestigationEditor({
  record,
  mode = "session",
  patientId,
  token,
  examinationId,
  canEdit,
  onCreated,
  onSaved,
  onDelete,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onError,
}) {
  const { palette } = useClinicTheme();

  const [createTetkik] = useCreateTetkikMutation();
  const [updateTetkik] = useUpdateTetkikMutation();
  const [listLabTests] = useListLabTestsMutation();
  const [listImages] = useListImagesMutation();

  const [pickerOpen, setPickerOpen] = useState(false);

  const createFn = useCallback(
    async (payload) => {
      const res = await createTetkik(payload).unwrap();
      return res?.data?.tetkik || res?.tetkik || res;
    },
    [createTetkik]
  );
  const updateFn = useCallback(
    async (payload) => {
      const res = await updateTetkik(payload).unwrap();
      return res?.data?.tetkik || res?.tetkik || res;
    },
    [updateTetkik]
  );

  // Standalone (Hasta Detay) modunda autosave kapalı, Kaydet butonu kullanılır.
  const useManualCreate = mode === "standalone";

  const { fieldValues, setField, saveStatus, saveNow } = useInlineAutosave({
    record,
    createMutation: createFn,
    updateMutation: updateFn,
    fieldsFromRecord: tetkikFieldsFromRecord,
    shouldCreate: (form) =>
      Boolean(
        (form.type === "laboratory" && form.laboratoryItems?.length) ||
          (form.type === "imaging" && form.imagingItems?.length) ||
          form.note
      ),
    debounceMs: 500,
    manualCreate: useManualCreate,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      type: form.type,
      status: form.status,
      note: form.note,
      ...(form.type === "laboratory"
        ? {
            laboratoryItems: (form.laboratoryItems || []).map((it) => ({
              labTest: it.labTest,
              name: it.name,
              unit: it.unit,
              referenceValue: it.referenceValue,
              resultValue: it.resultValue,
              notes: it.notes,
              isCompleted: Boolean(it.isCompleted),
            })),
          }
        : {}),
      ...(form.type === "imaging"
        ? {
            imagingItems: (form.imagingItems || []).map((it) => ({
              image: it.image,
              name: it.name,
              label: it.label,
              notes: it.notes,
              isCompleted: Boolean(it.isCompleted),
            })),
          }
        : {}),
    }),
    recordIdField: "tetkikId",
    onCreated,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  useEffect(() => {
    if (typeof onSaveStatusChange === "function")
      onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const isDisabled = !canEdit;
  const isNew = !record?._id;
  const statusOptions = isNew ? STATUS_OPTIONS_CREATE : STATUS_OPTIONS_ALL;
  const isLab = (fieldValues.type || "laboratory") === "laboratory";

  // ── Seçenek kaynakları (sunucuda sayfalı arama) ──
  const fetchLabTests = useMemo(
    () => async ({ query, page }) => {
      const res = await listLabTests({
        token,
        status: "active",
        query: query || undefined,
        page,
        limit: PICKER_PAGE_SIZE,
      }).unwrap();
      const list = res?.data?.labTests || res?.data?.items || [];
      const items = list.map((t) => ({
        key: String(t._id),
        value: String(t._id),
        title: t.name,
        subtitle: [t.unit, t.referenceValue && `Ref: ${t.referenceValue}`].filter(Boolean).join(" · ") || undefined,
        raw: t,
      }));
      return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
    },
    [listLabTests, token]
  );
  const fetchImages = useMemo(
    () => async ({ query, page }) => {
      const res = await listImages({
        token,
        status: "active",
        query: query || undefined,
        page,
        limit: PICKER_PAGE_SIZE,
      }).unwrap();
      const list = res?.data?.images || res?.data?.items || [];
      const items = list.map((t) => ({
        key: String(t._id),
        value: String(t._id),
        title: t.name || "—",
        raw: t,
      }));
      return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
    },
    [listImages, token]
  );

  // ── Kalem işlemleri ──
  // Drawer'dan gelen seçimleri tek seferde ekle (zaten olanları atla)
  const addLabItems = (options) => {
    const current = fieldValues.laboratoryItems || [];
    const seen = new Set(current.map((i) => String(i.labTest)));
    const next = [...current];
    options.forEach(({ raw: t }) => {
      if (!t?._id || seen.has(String(t._id))) return;
      seen.add(String(t._id));
      next.push({
        labTest: t._id,
        name: t.name || "",
        unit: t.unit || "",
        referenceValue: t.referenceValue || "",
        resultValue: "",
        notes: "",
        isCompleted: false,
      });
    });
    setField("laboratoryItems", next);
    setPickerOpen(false);
  };
  const addImgItems = (options) => {
    const current = fieldValues.imagingItems || [];
    const seen = new Set(current.map((i) => String(i.image)));
    const next = [...current];
    options.forEach(({ raw: t }) => {
      if (!t?._id || seen.has(String(t._id))) return;
      seen.add(String(t._id));
      next.push({ image: t._id, name: t.name || "", label: t.name || "", notes: "", isCompleted: false });
    });
    setField("imagingItems", next);
    setPickerOpen(false);
  };

  const listKey = isLab ? "laboratoryItems" : "imagingItems";
  const items = fieldValues[listKey] || [];
  const removeItem = (idx) => setField(listKey, items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) =>
    setField(listKey, items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));

  const hasItems = items.length > 0;
  const showFooter =
    !isDisabled && (useManualCreate || (!isNew && typeof onDelete === "function"));

  const smallField = (label, value, onChange) => (
    <FormTextField
      palette={palette}
      size="small"
      variant="standard"
      placeholder={label}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={isDisabled}
      inputProps={{ "aria-label": label }}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Yeni tetkik kaydı oluşturuluyor"
        editLabel="Tetkik kaydı düzenleniyor"
        // Standalone'da elle kaydedilir; durum rozeti yalnız otomatik kayıtta
        saveStatus={useManualCreate ? undefined : saveStatus}
        palette={palette}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Genel */}
        <Box sx={SECTION_SX}>
          <FieldRow label="Tetkik Tipi">
            <ChoiceChipGroup
              options={TYPE_OPTIONS}
              value={fieldValues.type || "laboratory"}
              onChange={(v) => v && setField("type", v)}
              // Tip yalnız yeni kayıtta seçilir
              disabled={isDisabled || !isNew}
              palette={palette}
              ariaLabel="Tetkik tipi"
            />
          </FieldRow>
          <FieldRow label="Durum">
            <FormSelect
              value={fieldValues.status || "requested"}
              options={statusOptions}
              onChange={(val) => setField("status", val)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
            />
          </FieldRow>
          <FieldRow label="Not">
            <FormTextField
              palette={palette}
              value={fieldValues.note || ""}
              onChange={(e) => setField("note", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Tetkik hakkında genel not…"
            />
          </FieldRow>
        </Box>

        {/* Tahliller / Görüntülemeler */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none", flex: 1, minHeight: "auto" }}>
          <SectionHeader
            title={isLab ? "Lab Tahlilleri" : "Görüntülemeler"}
            count={items.length}
            addLabel={isLab ? "Tahlil Ekle" : "Görüntü Ekle"}
            onAdd={canEdit ? () => setPickerOpen(true) : undefined}
            disabled={isDisabled}
            palette={palette}
          />
          <Box
            sx={{
              ...listAreaSx(undefined, items.length === 0),
              flex: 1,
              minHeight: ITEM_LIST_MIN_HEIGHT,
            }}
          >
            {items.length === 0 ? (
              <SectionEmpty
                icon={isLab ? <LabIcon /> : <ImagingIcon />}
                title={isLab ? "Henüz tahlil eklenmedi" : "Henüz görüntü eklenmedi"}
                hint={
                  canEdit
                    ? isLab
                      ? "İstenecek tahlilleri Tahlil Ekle ile listeden seçin."
                      : "İstenecek görüntülemeleri Görüntü Ekle ile listeden seçin."
                    : undefined
                }
              />
            ) : (
              <Box>
                {items.map((it, idx) => (
                  <Box key={`${listKey}-${idx}-${it.labTest || it.image}`} sx={ITEM_BLOCK_SX}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormCheckbox
                        size="small"
                        checked={!!it.isCompleted}
                        onChange={(checked) => updateItem(idx, "isCompleted", checked)}
                        disabled={isDisabled}
                        inputProps={{ "aria-label": `${it.name || "Kalem"} tamamlandı` }}
                      />
                      <Typography sx={{ ...ITEM_NAME_SX, flex: 1, minWidth: 0 }} noWrap title={it.name}>
                        {it.name || "—"}
                        {it.unit ? (
                          <Typography component="span" sx={{ fontSize: TYPO.label, color: COLORS.slate500, ml: 0.75 }}>
                            ({it.unit})
                          </Typography>
                        ) : null}
                      </Typography>
                      {it.referenceValue ? (
                        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, flexShrink: 0 }}>
                          Ref: {it.referenceValue}
                        </Typography>
                      ) : null}
                      {!isDisabled && (
                        <IconButton
                          size="small"
                          onClick={() => removeItem(idx)}
                          aria-label={`${it.name || "Kalem"} kaldır`}
                          sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                    {/* Sonuç / etiket ve not — tamamlandı kutusunun hizasında */}
                    <Box sx={{ display: "flex", gap: 1.5, pl: 4.5 }}>
                      {isLab
                        ? smallField("Sonuç", it.resultValue, (v) => updateItem(idx, "resultValue", v))
                        : smallField("Etiket", it.label, (v) => updateItem(idx, "label", v))}
                      {smallField("Not", it.notes, (v) => updateItem(idx, "notes", v))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Alt çubuk — standalone'da Kaydet, kayıtlıysa Sil */}
      {showFooter && (
        <Box
          sx={{
            flexShrink: 0,
            ...EDITOR_PX,
            py: 1.5,
            borderTop: `1px solid ${COLORS.slate100}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.25,
            bgcolor: COLORS.white,
          }}
        >
          {!isNew && typeof onDelete === "function" && (
            <ActionButton variant="delete" label="Sil" onClick={() => onDelete(record)} />
          )}
          {useManualCreate && (
            <ActionButton
              variant="save"
              palette={palette}
              onClick={() => saveNow()}
              disabled={!hasItems || saveStatus === "saving"}
              loading={saveStatus === "saving"}
              label="Kaydet"
            />
          )}
        </Box>
      )}

      <TagPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        icon={isLab ? <LabIcon /> : <ImagingIcon />}
        title={isLab ? "Tahlil Ekle" : "Görüntü Ekle"}
        subtitle={isLab ? "İstenecek laboratuvar tahlillerini seçin" : "İstenecek görüntülemeleri seçin"}
        fetchPage={isLab ? fetchLabTests : fetchImages}
        existing={items.map((i) => String(isLab ? i.labTest : i.image))}
        searchPlaceholder={isLab ? "Tahlil ara" : "Görüntüleme ara"}
        noun={isLab ? "tahlil" : "görüntüleme"}
        onSubmit={(_, options) => (isLab ? addLabItems(options) : addImgItems(options))}
      />
    </Box>
  );
}
